import { useState, useEffect } from "react";
import "./app.css"; // ✅ keep your custom css

// React Router
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// MUI
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Components
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";
import PrivateRoute from "PrivateRoute";

// Themes
import theme from "assets/theme";
import themeDark from "assets/theme-dark";

// Routes
import routes, { adminRoutes } from "routes";

// Context
import { useMaterialUIController, setMiniSidenav } from "context";

// Assets
import brandWhite from "assets/images/shipzzerTransparant.png";
import brandDark from "assets/images/ShipzzerLogo.png";

// Store & actions
import { loadUser } from "actions/auth";
import { Provider } from "react-redux";
import configureStore from "./store/store";
import { getVBSVersion } from "helpers/helpers";
import { URL } from "./config/config";

// i18n
import { withTranslation } from "react-i18next";

// Toastify
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export const store = configureStore();

const App = () => {
  toast.configure();

  const { pathname } = useLocation();
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;

  const [onMouseEnter, setOnMouseEnter] = useState(false);

  const loading = store.getState().auth.loading;
  const loggedUser = store.getState().auth.loggedUser;

  // Setup language
  useEffect(() => {
    if (!localStorage.getItem("prefLanguage")) {
      localStorage.setItem("prefLanguage", "en");
    }
  }, []);

  // Load user info
  useEffect(() => {
    store.dispatch(loadUser());
  }, []);

  // Load version
  useEffect(() => {
    const getVersion = async () => {
      store.dispatch(await getVBSVersion(URL.API_URL));
    };
    getVersion();
  }, []);

  // Dir update
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Scroll reset on route change
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  // Hover sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Define public routes (no sidebar)
  const publicRoutes = ["/authentication/sign-in", "/reset-password"];

  // Map routes
  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) return getRoutes(route.collapse);

      if (publicRoutes.includes(route.route)) {
        return (
          <Route path={route.route} element={route.component} key={route.key} />
        );
      }

      return (
        <Route
          exact
          path={route.route}
          element={<PrivateRoute>{route.component}</PrivateRoute>}
          key={route.key}
        />
      );
    });

  return (
    <Provider store={store}>
      <ThemeProvider theme={darkMode ? themeDark : theme}>
        <CssBaseline />

        {/* Sidebar only if NOT on public route */}
        {layout === "dashboard" &&
          loggedUser &&
          !publicRoutes.includes(pathname) && (
            <Sidenav
              color={sidenavColor}
              brand={
                (transparentSidenav && !darkMode) || whiteSidenav
                  ? brandDark
                  : brandWhite
              }
              brandName="Shipzzer Trucker"
              routes={
                !loading && loggedUser?.roles?.includes("adminVBS")
                  ? adminRoutes
                  : routes
              }
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
          )}

        {layout === "vr" && <Configurator />}

        <Routes>
          {/* All mapped routes */}
          {!loading && loggedUser?.roles?.includes("adminVBS")
            ? getRoutes(adminRoutes)
            : getRoutes(routes)}

          {/* Catch-all redirect */}
          <Route
            path="*"
            element={
              !loading && loggedUser ? (
                loggedUser.roles.includes("adminVBS") ? (
                  <Navigate to="/truck-companies" />
                ) : (
                  <Navigate to="/dashboard" />
                )
              ) : (
                <Navigate to="/authentication/sign-in" />
              )
            }
          />
        </Routes>
      </ThemeProvider>
    </Provider>
  );
};

export default withTranslation()(App);
